import React, { Component, ErrorInfo, ReactNode } from 'react';
// Lazy-loaded: keeps errorTrackingService out of the main bundle (~17KB saved).
// Error reporting is fire-and-forget — the small delay from dynamic import is fine.
const reportErrorBoundary = (error: Error, componentName?: string) =>
  import('../services/errorTrackingService').then(m => m.reportErrorBoundary(error, componentName));

// =============================================================================
// Props & State
// =============================================================================

interface Props {
  /** Name of the section/component being guarded — included in the crash report. */
  componentName?: string;
  /**
   * Optional custom fallback UI. Receives the error so it can be displayed.
   * If omitted, a minimal generic message is shown (no stack trace to the user).
   */
  fallback?: (error: Error) => ReactNode;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// =============================================================================
// ErrorBoundary
//
// Catches render-phase and lifecycle errors inside the subtree.
// React error boundaries MUST be class components — hooks cannot do this.
//
// Usage (wrap the whole router tree in App.tsx):
//   <ErrorBoundary componentName="App">
//     <Router>...</Router>
//   </ErrorBoundary>
//
// Or wrap individual high-risk pages for finer-grained recovery:
//   <ErrorBoundary componentName="ApplicationDetail" fallback={(e) => <ErrorPage error={e} />}>
//     <ApplicationDetail />
//   </ErrorBoundary>
//
// @author HipoTech Development Team
// @version 1.0.0
// =============================================================================

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // ChunkLoadError means a lazy chunk was requested but no longer exists on the
    // server — almost always caused by a new deployment invalidating old chunk hashes
    // while the user still holds a cached main bundle. The only real recovery is a
    // full page reload, which fetches the new main.js and correct chunk references.
    // We do NOT report these to monitoring — they are deployment artifacts, not bugs.
    if (error.name === 'ChunkLoadError') {
      window.location.reload();
      return;
    }

    // Send to the monitoring pipeline. This ends up in ExceptionTrackingService
    // .trackFrontendException() → same DB tables and alert emails as backend crashes.
    reportErrorBoundary(error, this.props.componentName);

    // Also log locally so it shows up in dev-tools console alongside React's own output.
    console.error(
      `[ErrorBoundary${this.props.componentName ? ' / ' + this.props.componentName : ''}]`,
      error,
      info.componentStack
    );
  }

  handleReset = (): void => {
    // For ChunkLoadErrors, a state reset isn't enough — reload to get fresh chunk
    // references. For all other errors, reset React state as normal.
    if (this.state.error?.name === 'ChunkLoadError') {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (!this.state.hasError || !this.state.error) {
      return this.props.children;
    }

    // Custom fallback takes priority
    if (this.props.fallback) {
      return this.props.fallback(this.state.error);
    }

    // Default fallback — deliberately minimal, no stack trace shown to users.
    // CRITICAL: Do NOT use translate() here — the i18n system itself may be
    // what caused the crash. All text must be hardcoded.
    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '40vh',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'inherit',
        }}
      >
        <h2 style={{ marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: 600, color: '#b91c1c' }}>
          Something went wrong
        </h2>
        <p style={{ color: '#666', marginBottom: '1.5rem', maxWidth: '400px' }}>
          An unexpected error occurred. Our team has been notified automatically.
        </p>
        <button
          onClick={this.handleReset}
          style={{
            padding: '0.5rem 1.25rem',
            borderRadius: '6px',
            border: '1px solid #ccc',
            background: 'white',
            cursor: 'pointer',
            fontSize: '0.9rem',
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;